<template>
  <page v-if="stylesLoaded" class="full-height">
    <div>
      <div
        class="flex items-end justify-between page-header-content pb-4 md:items-start"
      >
        <div class="flex-col align-items-center flex-wrap">
          <h1 class="schedule-board-title mr-2 page-header-1">Schedule</h1>
          <h1 class="schedule-board-date page-header-2">{{ title }}</h1>
        </div>
        <div class="flex md:items-center md:gap-4">
          <AppointmentColorsLegend />
          <div class="hidden md:flex">
            <SlotWrapper>
              <template #actions>
                <schedule-board-filters
                  class="schedule-board-filters"
                ></schedule-board-filters>
              </template>
            </SlotWrapper>
          </div>
          <div class="md:hidden">
            <button
              type="button"
              class="text-black bg-white border hover:bg-blue-800 hover:text-white focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-full text-lg p-2.5 text-center inline-flex items-center me-2 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
              @click="toggleFilterModal"
            >
              <i class="fa-regular fa-filter"></i>
            </button>
          </div>
          <CheckAvailabilityModal class="md:hidden" />
        </div>
        <Modal
          v-if="filterModal"
          title="Filter"
          @hide-modal="toggleFilterModal"
          @submit="toggleFilterModal"
        >
          <div>
            <SlotWrapper>
              <template #actions>
                <schedule-board-filters
                  class="schedule-board-filters"
                ></schedule-board-filters>
              </template>
            </SlotWrapper>
          </div>
        </Modal>
      </div>
      <schedule-board class="schedule-board mt-4"></schedule-board>
      <div
        v-if="drawerOpen"
        class="fixed inset-0 z-50 bg-gray-900/50"
        @click="handleDrawerClosed"
      ></div>
      <AppointmentDetailsDrawer
        v-if="drawerOpen"
        :key="selectedAppointmentId"
        :appointment-id="selectedAppointmentId"
        :backdrop="false"
        @hide-drawer="handleDrawerClosed"
        @appointment-updated="handleAppointmentUpdated"
      />
      <div class="fixed bottom-12 right-4 z-10 flex gap-3 md:hidden">
        <CreateNewFOB />
      </div>
    </div>
  </page>
</template>

<script setup lang="ts">
import moment from "moment-timezone";
import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref } from "vue";
import { useStore } from "vuex";
import cable from "@/lib/cable.js";
import { EventBus } from "@/lib/event_bus.js";
import { getQueryVariables, updateQueryParam } from "@/lib/url.js";

import Modal from "@/components/Modal.vue";
import Page from "@/components/Page.vue";
import SlotWrapper from "@/components/SlotWrapper";
import AppointmentDetailsDrawer from "@/AppointmentDetails/AppointmentDetailsDrawer.vue";
import VueChannel from "@/channels/VueChannel";
import AppointmentColorsLegend from "@/Scheduler/AppointmentColorsLegend.vue";
import CreateNewFOB from "@/SchedulerV2/CreateNewFOB.vue";
import CheckAvailabilityModal from "./CheckAvailabilityModal.vue";
import ScheduleBoard from "./ScheduleBoard.vue";
import ScheduleBoardFilters from "./ScheduleBoardFilters.vue";

const store = useStore();
const stylesLoaded = ref(false);
const filterModal = ref(false);
const selectedAppointmentId = ref<string | number | null>(null);
let channel: VueChannel | null = null;
const title = computed(() =>
  moment(store.state.scheduleBoardFilters.start).format("ddd MMM Do, YYYY")
);
const drawerOpen = computed(() => Boolean(selectedAppointmentId.value));

onBeforeMount(async () => {
  try {
    await import("./scheduler_tailwind.css");
    stylesLoaded.value = true;
  } catch (e) {
    stylesLoaded.value = true;
  }
});
const handleDrawerOpen = (appointmentId: string | number) => {
  selectedAppointmentId.value = appointmentId;
};
const handleDrawerClosed = () => {
  selectedAppointmentId.value = null;
  updateQueryParam("appointment_id", null);
};
const handleAppointmentUpdated = () => {
  EventBus.$emit("refresh-schedule-board");
};

onMounted(() => {
  store.dispatch("fetchDepartments");
  EventBus.$on("open-appointment-drawer", handleDrawerOpen);
  EventBus.$on("appointment-updated", handleAppointmentUpdated);
  const { appointment_id } = getQueryVariables();
  if (appointment_id) {
    handleDrawerOpen(appointment_id);
  }
  window.handleSchedulerEventEdit = handleDrawerOpen;

  channel = new VueChannel();
  cable.subscribe(channel).catch((err) => {
    console.log("Unable to subscribe to VueChannel", err);
  });
});

onBeforeUnmount(() => {
  EventBus.$off("open-appointment-drawer", handleDrawerOpen);
  EventBus.$off("appointment-updated", handleAppointmentUpdated);
  channel?.disconnect();
  window.handleSchedulerEventEdit = null;
});

const toggleFilterModal = () => (filterModal.value = !filterModal.value);

defineExpose({ stylesLoaded });
</script>

<style lang="scss" scoped>
.schedule-board-filters {
  z-index: 2;
}
.schedule-board {
  z-index: 1;
  position: sticky;
  top: 0;
}

:deep(.row) {
  height: 100%;
}
.schedule-board-date {
  font-weight: 400 !important;
  color: var(--font-body-color);
}
</style>
